"use client";

import clsx from "clsx";
import {useTranslation} from "react-i18next";
import type {CardComponentProps} from "nextstepjs";

export default function GailGptWelcomeTourCard({
   step,
   currentStep,
   totalSteps,
   nextStep,
   prevStep,
   skipTour,
   arrow,
}: Readonly<CardComponentProps>) {
   const {t} = useTranslation("assistants");
   const isFloating = !step.selector;
   const isLastStep = currentStep === totalSteps - 1;

   return (
      <div
         className={clsx(
            "bg-panel rounded-xl p-5 shadow-lg border border-gpt-border max-w-md",
            isFloating ? "min-w-80" : "min-w-64"
         )}
      >
         <div className="flex items-start justify-between gap-4 mb-3">
            <div>
               <p className="text-xs font-semibold uppercase tracking-wide text-gpt-accent">
                  {t("onboarding.label")}
               </p>
               <h2 className="mt-1 text-lg font-semibold text-900">{step.title}</h2>
            </div>
            {step.icon && (
               <span key={currentStep} className="text-2xl text-gpt-accent">
                  {step.icon}
               </span>
            )}
         </div>

         <div className="mb-5 text-sm leading-6 text-700">{step.content}</div>

         <div className="mb-5 h-2 rounded-full bg-200">
            <div
               className="h-2 rounded-full bg-gpt-accent transition-all"
               style={{width: `${((currentStep + 1) / totalSteps) * 100}%`}}
            />
         </div>

         <div className="flex items-center justify-between gap-3 text-xs">
            <button
               type="button"
               onClick={prevStep}
               disabled={currentStep === 0}
               className={clsx(
                  "px-3 py-2 font-medium rounded-md bg-200 text-600 cursor-pointer",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  step.showControls ? "block" : "hidden"
               )}
            >
               {t("onboarding.back")}
            </button>

            <span className="text-500 whitespace-nowrap">
               {t("onboarding.progress", {current: currentStep + 1, total: totalSteps})}
            </span>

            <button
               type="button"
               onClick={nextStep}
               className={clsx(
                  "px-3 py-2 font-medium rounded-md cursor-pointer text-white",
                  isLastStep ? "bg-green-500" : "bg-gpt-accent",
                  step.showControls ? "block" : "hidden"
               )}
            >
               {isLastStep ? t("onboarding.finish") : t("onboarding.next")}
            </button>
         </div>

         {arrow}

         {skipTour && currentStep < totalSteps - 1 && (
            <button
               type="button"
               onClick={skipTour}
               className={clsx(
                  "mt-3 w-full px-3 py-2 text-xs font-medium rounded-md bg-200 text-600 cursor-pointer",
                  step.showSkip ? "block" : "hidden"
               )}
            >
               {t("onboarding.skip")}
            </button>
         )}
      </div>
   );
}
